"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaFilter,
  FaGraduationCap,
  FaToggleOn,
  FaToggleOff,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFileExcel,
  FaSpinner,
} from "react-icons/fa"
import { FiFilter, FiX, FiRefreshCw, FiSearch as FiSearchIcon, FiBookOpen, FiCalendar, FiSliders } from "react-icons/fi"
import { Pagination as PaginationComponent } from "../../../utils/pagination.utils"
import { TrainingType, Pagination } from "../types"
import { useAuth } from "../../../provider/auth.provider"
import { ExcelDownload } from "../../../utils/excel.utils"
import toast from "react-hot-toast"
import api from "../../../libs/axios"
import { useDebouncedCallback } from "../../../hooks/useDebounce"

type TrainingTypeExportRow = Record<string, string | number | boolean | null>

export interface TypeFilters {
  [key: string]: string | undefined
  search?: string
  is_active?: string
  training_name?: string
  training_description?: string
  validity_min?: string
  validity_max?: string
  created_from?: string
  created_to?: string
}

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  filters: TypeFilters
  onFiltersChange: (filters: TypeFilters) => void
  onApply: () => void
  onReset: () => void
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, filters, onFiltersChange, onApply, onReset }) => {
  const [activeTab, setActiveTab] = useState("basic")

  useEffect(() => {
    if (isOpen) {
      setActiveTab("basic")
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleInputChange = (field: keyof TypeFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    })
  }

  const tabs = [
    { id: "basic", label: "Basic Info", icon: FiSearchIcon },
    { id: "validity", label: "Validity", icon: FiSliders },
    { id: "dates", label: "Dates", icon: FiCalendar },
  ]

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-3xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id ? "border-blue-500 text-blue-600 bg-white" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Search by name or description"
                  value={filters.search || ""}
                  onChange={(e) => handleInputChange("search", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  value={filters.is_active || ""}
                  onChange={(e) => handleInputChange("is_active", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Training name"
                  value={filters.training_name || ""}
                  onChange={(e) => handleInputChange("training_name", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Training description"
                  value={filters.training_description || ""}
                  onChange={(e) => handleInputChange("training_description", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "validity" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validity (Min Months)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., 6"
                  value={filters.validity_min || ""}
                  onChange={(e) => handleInputChange("validity_min", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validity (Max Months)</label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., 12"
                  value={filters.validity_max || ""}
                  onChange={(e) => handleInputChange("validity_max", e.target.value)}
                />
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ""}
                  onChange={(e) => handleInputChange("created_from", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ""}
                  onChange={(e) => handleInputChange("created_to", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearchIcon className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface TypesListProps {
  types: TrainingType[]
  pagination: Pagination
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAdd: () => void
  onEdit: (type: TrainingType) => void
  onToggleStatus: (type: TrainingType) => void
  loading?: boolean
  sortConfig?: { field: string; direction: "ASC" | "DESC" }
  onSort?: (field: string, direction: "ASC" | "DESC") => void
  onAdvancedFilter?: (filters: TypeFilters) => void
  advancedFilters?: TypeFilters
}

export const TypesList: React.FC<TypesListProps> = ({
  types,
  pagination,
  onSearch,
  onStatusFilter,
  onPageChange,
  onLimitChange,
  onAdd,
  onEdit,
  onToggleStatus,
  loading = false,
  sortConfig,
  onSort,
  onAdvancedFilter,
  advancedFilters = {},
}) => {
  const { hasPermission } = useAuth()
  const canCreateType = hasPermission("TrainingType", "create")
  const canEditType = hasPermission("TrainingType", "edit")
  const canToggleStatusType = hasPermission("TrainingType", "toggle_status")

  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [filters, setFilters] = useState<TypeFilters>(advancedFilters)
  const [showExcelModal, setShowExcelModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<TrainingTypeExportRow[]>([])

  useEffect(() => {
    setFilters(advancedFilters)
  }, [advancedFilters])

  const getActiveFiltersCount = () =>
    Object.entries(advancedFilters).filter(([_, value]) => value !== undefined && value !== "").length

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => { const value = e.target.value; setSearchTerm(value); debouncedOnSearch(value) }
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => { const value = e.target.value; setStatusFilter(value); onStatusFilter(value) }

  const handleFiltersChange = (updatedFilters: TypeFilters) => {
    setFilters(updatedFilters)
  }

  const handleResetFilters = () => {
    setFilters({})
    onAdvancedFilter?.({})
  }

  const handleApplyFilters = () => {
    onAdvancedFilter?.(filters)
    setIsFilterModalOpen(false)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
  }

  const formatDateTimeValue = (dateString?: string | null) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return "-"
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusLabel = (value?: boolean) => (value ? "Active" : "Inactive")

  const getCreatorName = (creator?: { first_name?: string; last_name?: string } | null) => {
    if (!creator) return "-"
    return [creator.first_name, creator.last_name].filter(Boolean).join(" ") || "-"
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number | boolean> = { page, limit }

    if (searchTerm.trim()) params.search = searchTerm.trim()
    if (statusFilter) params.is_active = statusFilter

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value
      }
    })

    if (sortConfig?.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const mapTypeForExport = (type: TrainingType): TrainingTypeExportRow => {
    return {
      "Course Name": type.training_name,
      Description: type.training_description || "-",
      "Validity (Months)": type.validity_months ?? "-",
      Status: getStatusLabel(type.is_active),
      "Created By": getCreatorName(type.creator),
      "Creator Email": type.creator?.email || "-",
      "Created At": formatDateTimeValue(type.created_at),
      "Updated At": formatDateTimeValue(type.updated_at),
    }
  }

  const fetchExportData = async () => {
    if (exporting) return

    try {
      setExporting(true)
      setExportData([])
      const pageSize = 100
      let page = 1
      let hasMore = true
      const allTypes: TrainingType[] = []

      while (hasMore) {
        const params = buildExportParams(page, pageSize)
        const response = await api.get("/training/courses/list", { params })

        if (!response.data?.success) {
          throw new Error(response.data?.message || "Failed to fetch training types")
        }

        const data = response.data.data || {}
        const fetched: TrainingType[] = data.training_types || []
        const paginationInfo = data.pagination

        allTypes.push(...fetched)

        const nextPageAvailable = paginationInfo?.has_next_page ??
          (paginationInfo?.total_pages ? page < paginationInfo.total_pages : fetched.length === pageSize)

        if (!fetched.length || !nextPageAvailable) {
          hasMore = false
        } else {
          page += 1
        }
      }

      if (!allTypes.length) {
        toast.error("No training types found for export")
        return
      }

      setExportData(allTypes.map(mapTypeForExport))
      setShowExcelModal(true)
    } catch (error: any) {
      console.error("Error exporting training types:", error)
      toast.error(error?.response?.data?.message || "Failed to prepare export data")
    } finally {
      setExporting(false)
    }
  }

  const handleColumnSort = (field: string) => {
    if (!onSort) return
    const direction = sortConfig?.field === field && sortConfig?.direction === "ASC" ? "DESC" : "ASC"
    onSort(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig?.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
    if (sortConfig.direction === "ASC") return <FaSortUp className="w-3 h-3 text-blue-600" />
    return <FaSortDown className="w-3 h-3 text-blue-600" />
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Courses</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              {exporting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <FaFileExcel className="w-4 h-4" />
                  Export Excel
                </>
              )}
            </button>
            {canCreateType && (
              <button onClick={onAdd} className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm">
                <FaPlus className="w-4 h-4" /> Add Course
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input type="text" placeholder="Search by name..." value={searchTerm} onChange={handleSearchChange} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select value={statusFilter} onChange={handleStatusFilterChange} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white">
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="w-full sm:w-auto flex items-end">
            <button
              type="button"
              onClick={() => setIsFilterModalOpen(true)}
              className={`inline-flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2 border rounded text-sm font-medium transition-colors ${
                getActiveFiltersCount() > 0 ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                <button type="button" onClick={() => handleColumnSort("training_name")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Course Name {getSortIcon("training_name")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[250px]">
                <button type="button" onClick={() => handleColumnSort("training_description")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Description {getSortIcon("training_description")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">
                <button type="button" onClick={() => handleColumnSort("validity_months")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Validity (Months) {getSortIcon("validity_months")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[80px]">
                <button type="button" onClick={() => handleColumnSort("is_active")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Status {getSortIcon("is_active")}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <button type="button" onClick={() => handleColumnSort("created_at")} className="flex items-center gap-2 text-left uppercase tracking-wider">
                  Created At {getSortIcon("created_at")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div><span className="text-sm text-gray-600">Loading types...</span></div></td></tr>
            ) : types.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center"><div className="flex flex-col items-center"><div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3"><FaGraduationCap className="w-6 h-6 text-gray-400" /></div><p className="text-sm text-gray-500">No courses found</p></div></td></tr>
            ) : (
              types.map((type) => (
                <tr key={type.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {canEditType && (
                        <button onClick={() => onEdit(type)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors" title="Edit">
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {canToggleStatusType && (
                        <button onClick={() => onToggleStatus(type)} className={`p-2 rounded transition-colors ${type.is_active ? 'text-green-600 hover:text-green-800 hover:bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`} title={type.is_active ? "Deactivate" : "Activate"}>
                          {type.is_active ? <FaToggleOn className="w-5 h-5" /> : <FaToggleOff className="w-5 h-5" />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap"><p className="text-sm font-semibold text-gray-900">{type.training_name}</p></td>
                  <td className="px-4 py-4"><span className="text-sm text-gray-600 line-clamp-2">{type.training_description || "-"}</span></td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{type.validity_months ? `${type.validity_months} months` : "-"}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${type.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{type.is_active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(type.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationComponent pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="training_types"
        title="Export Courses"
        description="Download comprehensive course details"
      />
    </div>
  )
}
